import { MagnifyingGlass } from "@phosphor-icons/react"
import {
  type ColumnDef,
  type ColumnSizingState,
  flexRender,
  getCoreRowModel,
  type SortingState,
  type Row as TableRowType,
  useReactTable,
} from "@tanstack/react-table"
import {
  defaultRangeExtractor,
  useVirtualizer,
  useWindowVirtualizer,
} from "@tanstack/react-virtual"
import {
  Text,
  type TextProps,
} from "@tokenterminal/core/ui/primitives/text/Text"
import { Skeleton } from "@tokenterminal/core/ui/progress/skeleton/Skeleton"
import * as CoreTable from "@tokenterminal/core/ui/table/Table"
import { toDictionary } from "@tokenterminal/core/utils/toDictionary"
import { trpcClient } from "@tokenterminal/core/utils/trpc"
import { type PresetColumnConfig } from "@tokenterminal/tt-analytics-api-types/dist/api/projects"
import { type ScreenerColumns } from "@tokenterminal/tt-types"
import { Box } from "@tokenterminal/ui/Box"

import { useIsMobile } from "@tokenterminal/ui/hooks/isMobile"
import { Link } from "@tokenterminal/ui/Link/Link"
import { overflowTextCss } from "@tokenterminal/ui/Text/Text.css"
import { colorsV4 } from "@tokenterminal/ui/theme/themeV4/colorsContract.css"
import clsx from "clsx"
import { ProjectLogo } from "components/project/ProjectLogo"
import { DEFAULT_NUMBER_FORMAT } from "components/v3/Header/AccountDropdown/NumberFormatSwitcher"
import { projectLogosRowOverride } from "components/v3/ProjectLogos/ProjectLogos.css"
import { ProjectLogosSkeleton } from "components/v3/ProjectLogos/ProjectLogosSkeleton"

import { useSocket } from "context/SocketContext"
import { useNumberFormatter } from "hooks/useNumberFormatter"
import { useAtom } from "jotai"
import { capitalize } from "lodash"
import EmptyState from "modules/account/views/Team/EmptyState"
import { useMarketSectorsList } from "modules/chart-builder/components/SeriesTab/SerieSelector/FilterSelector/useMarketSectorsList"
import { useMetricsConfigurationList } from "modules/projects/hooks/useMetricsConfigurationList"
import StandardizedListingIcon from "modules/v3/ProjectHero/StandardizedListingIcon"
import {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react"
import { useIntersectionObserver } from "utils/useIntersectionObserver"
import { MiniChartInlineSparkline } from "./MiniChartSparkline"
import { type FiltersAtomType } from "./ScreenerTable"
import {
  leftPinnedColumnCss,
  projectListCellCss,
  projectListHeaderCss,
  rightPinnedColumnCss,
  tableContainerCss,
  tableNumberCss,
} from "./ScreenerTable.css"
import { marketSectorCss } from "./Table.css"
import { getCommonPinningStyles, TableHeader } from "./TableHeader"
import { CHART_VALUE_CELL_DATA_TYPES } from "./utils/cellFormatHelper"
import { VirtualPadding } from "./VirtualPadding"

const LazyProjectChains = lazy(() =>
  import("components/v3/ProjectChains").then((mod) => ({
    default: mod.ProjectChains,
  }))
)

const ROWS_PER_FETCH = 50

const sizes: Record<
  "expanded" | "compact",
  {
    PROJECT_LOGO_SIZE: number
    ROW_HEIGHT: number
    MINI_CHART_HEIGHT: number
    FONT_SIZE: TextProps["size"]
  }
> = {
  expanded: {
    PROJECT_LOGO_SIZE: 24,
    ROW_HEIGHT: 57,
    MINI_CHART_HEIGHT: 24,
    FONT_SIZE: "14",
  },
  compact: {
    PROJECT_LOGO_SIZE: 18,
    ROW_HEIGHT: 36.5,
    MINI_CHART_HEIGHT: 20,
    FONT_SIZE: "inherit",
  },
}

const TYPES = {
  String: "String",
  Number: "Number",
  ArrayString: "Array(String)",
}

const OPERATORS = {
  like: "like",
  in: "in",
  hasAny: "hasAny",
}

export const leftAlignedColumns = [
  "name",
  "project_name",
  "market_sector_id",
  "chains",
  "chain",
  "market_sectors",
]

const aggregationColumns = [
  "sum",
  "avg",
  "latest",
  "ath",
  "atl",
  "change",
  "trend",
  "sparkline",
]

export const fetchProjectArgs = async ({
  marketSectors,
  chains,
  search,
  columns,
  sorting,
}: {
  marketSectors: string[]
  chains: string[]
  search: string
  columns: PresetColumnConfig[]
  sorting: SortingState
}) => {
  let sort
  const filters: Record<string, any> = {}

  if (sorting.length) {
    sort = {
      column: sorting[0]?.id,
      desc: sorting[0]?.desc,
    }
  }

  if (search) {
    const searchFilter = {
      type: TYPES.String,
      value: search,
      operator: OPERATORS.like,
    }
    filters.name = { or: [searchFilter] }
    filters.project_id = { or: [searchFilter] }
  }

  if (marketSectors.length) {
    filters.market_sectors = {
      and: [
        {
          type: TYPES.ArrayString,
          value: marketSectors,
          operator: OPERATORS.hasAny,
        },
      ],
    }
  }

  if (chains.length) {
    filters.chains = {
      and: [
        {
          type: TYPES.ArrayString,
          value: chains,
          operator: OPERATORS.hasAny,
        },
      ],
    }
  }

  const cols = columns.map((c) => {
    const field = c.field.split("_")

    const aggregation = field.pop()
    const interval = field.pop()
    const metric = field.join("_")

    if (aggregationColumns.includes(aggregation ?? "")) {
      return {
        metric,
        interval,
        aggregation: c.sparkline ? "sparkline" : aggregation,
      }
    }

    if (c.field === "market_sector_id") {
      return {
        name: "market_sectors",
      }
    }

    return {
      name: c.field,
    }
  }) as ScreenerColumns

  return {
    columns: cols,
    sorting: sort,
    filters,
  }
}

export const fetchProjectsList = async (
  {
    marketSectors,
    chains,
    search,
    columns,
    sorting,
  }: {
    marketSectors: string[]
    chains: string[]
    search: string
    columns: PresetColumnConfig[]
    sorting: SortingState
  },
  limit: number,
  offset: number
) => {
  const {
    columns: cols,
    sorting: sort,
    filters,
  } = await fetchProjectArgs({
    marketSectors,
    chains,
    search,
    columns,
    sorting,
  })

  return trpcClient.projects.postScreenerTable.mutate({
    columns: cols,
    sorting: sort,
    filters,
    offset,
    limit,
  })
}

function LiveValueCell({
  fontSize,
  value,
  projectId,
  cellDataType,
}: {
  fontSize: TextProps["size"]
  value: number
  projectId: string
  cellDataType: string
}) {
  const socketData = useSocket()

  const realtimeValue = socketData.priceMap[projectId]?.usd

  const priceValue = CHART_VALUE_CELL_DATA_TYPES[cellDataType](
    realtimeValue || value
  )

  return (
    <Text size={fontSize} className={tableNumberCss}>
      <Box as="span" className={overflowTextCss}>
        {priceValue === "0" || priceValue === "0.0%" ? "" : priceValue}
      </Box>
    </Text>
  )
}

const placeholderArray = Array.from({ length: 50 }, () => ({
  placeholder: true,
}))

export function TableComponent({
  columns,
  filtersAtom,
  compact,
  tableContainerRef,
}: {
  columns: PresetColumnConfig[]
  filtersAtom: FiltersAtomType
  compact: boolean
  tableContainerRef: React.RefObject<HTMLDivElement>
}) {
  const numberFormat = useNumberFormatter()
  const { data: marketSectors = [] } = useMarketSectorsList()
  const marketSectorDictionary = toDictionary(marketSectors, "id")
  const { data: metrics = [] } = useMetricsConfigurationList()
  const metricDictionary = toDictionary(metrics, "metric_id")

  const [projects, setProjects] = useState<any[]>(placeholderArray)
  const [isFetching, setIsFetching] = useState(false)
  const [hasMore, setHasMore] = useState(true)
  const [offset, setOffset] = useState(0)

  const [filters, setFilters] = useAtom(filtersAtom)
  const [columnSizing, setColumnSizing] = useState<ColumnSizingState>(
    filters.columnSizing
  )

  const size = compact ? sizes.compact : sizes.expanded

  const height = size.ROW_HEIGHT
  const projectLogoSize = size.PROJECT_LOGO_SIZE
  const fontSize = size.FONT_SIZE
  const miniChartHeight = size.MINI_CHART_HEIGHT

  useEffect(() => {
    setColumnSizing(filters.columnSizing)
  }, [filters.columnSizing])

  useEffect(() => {
    if (filters.columns.length === 0) return

    setIsFetching(true)
    fetchProjectsList(filters, ROWS_PER_FETCH, 0).then((data) => {
      setIsFetching(false)
      if (!data) return

      if (!data.data.length) {
        setHasMore(false)
        setProjects([])
        return
      }

      setProjects(data.data)
      setOffset(ROWS_PER_FETCH)
      setHasMore(data.rows_before_limit_at_least! > ROWS_PER_FETCH)
    })
  }, [filters])

  const tableColumns = useMemo(() => {
    return [
      {
        field: "name",
        display: "Project",
        default_size: 270,
        sparkline: false,
        cell_data_type: "string",
        group: null,
        metric_slug: null,
      },
      ...filters.columns.map((column) => {
        const columnDefinition = columns.find(
          (c) => c.field === column.field
        ) as PresetColumnConfig

        if (columnDefinition) {
          return { ...columnDefinition, sparkline: column.sparkline }
        }

        return column
      }),
    ]
      ?.map((column) => {
        if (!column) return null

        let field = column.field

        if (column.sparkline) {
          const fieldParts = column.field.split("_")
          const aggregation = fieldParts.pop()
          const interval = fieldParts.pop()
          const metric = fieldParts.join("_")

          field = `${metric}_${interval}_sparkline`
        }

        // TODO: Get ecosystem metrics in screener api
        if (!column.display) {
          const fieldParts = column.field.split("_")

          const aggregation = fieldParts.pop()
          const interval = fieldParts.pop()
          const metric = fieldParts.join("_")

          const metricName = metricDictionary.get(metric)?.title

          column.display = metricName
            ? `${capitalize(aggregation)} ${interval}`
            : undefined
          column.group = metricName

          const length = Math.max(
            column.group?.length ?? 0,
            column.display?.length ?? 0,
            column.field.length
          )

          column.default_size = Math.round(40 * Math.pow(length, 0.5))
        }

        const fieldKey = `${field}${column.sparkline ? "_sparkline" : ""}`

        return {
          id: fieldKey,
          accessorKey: field,
          header: column.display,
          group: column.group,
          size: column.default_size,
          cell: ({ row }: { row: TableRowType<any> }) => {
            const value = row.original[field as string]

            if (column?.sparkline && column?.metric_slug) {
              return (
                <Suspense fallback={null}>
                  <MiniChartInlineSparkline
                    value={value}
                    height={miniChartHeight}
                  />
                </Suspense>
              )
            }

            switch (field) {
              case "name":
                return (
                  <Link href={`/projects/${row.original.slug}`}>
                    <div className="flex gap-2 items-center justify-start">
                      <Text
                        color="secondary"
                        flexShrink={0}
                        style={{
                          width: "1.9em",
                          textAlign: "right",
                          fontVariantNumeric: "tabular-nums",
                        }}
                      >
                        {row.index + 1}
                      </Text>
                      {row.original.placeholder ? (
                        <Skeleton
                          className="rounded-full"
                          style={{
                            width: projectLogoSize,
                            height: projectLogoSize,
                          }}
                        />
                      ) : (
                        <ProjectLogo
                          logo={row.original.logo}
                          name={row.original.name}
                          size={projectLogoSize}
                          borderRadius="rounded"
                          flexShrink={0}
                        />
                      )}
                      <div className="flex gap-2 items-center justify-start">
                        {row.original.placeholder ? (
                          <Skeleton
                            style={{
                              width: row.index % 2 === 0 ? "90px" : "110px",
                              height: "14px",
                            }}
                          />
                        ) : (
                          <Text size="14" fontWeight="500">
                            <Box as="span" className={overflowTextCss}>
                              {row.original.name}
                            </Box>
                          </Text>
                        )}
                        {row.original?.symbol && (
                          <Text
                            color="secondary"
                            size="14"
                            style={{
                              fontFamily: "var(--font-geist-mono)",
                              opacity: 0.75,
                              whiteSpace: "nowrap",
                            }}
                          >
                            {row.original.symbol}
                          </Text>
                        )}
                        {row.original?.tier === "standard" && (
                          <StandardizedListingIcon />
                        )}
                      </div>
                    </div>
                  </Link>
                )
              case "market_sectors":
                return row.original.placeholder ? (
                  <Skeleton
                    style={{
                      width: "100px",
                      height: "14px",
                    }}
                  />
                ) : (
                  <Link
                    onClick={(e) => {
                      e.stopPropagation()
                    }}
                    href={`/markets/${row.original.market_sectors[0]}`}
                    className={marketSectorCss}
                    style={{
                      color: colorsV4.fg.secondary,
                    }}
                  >
                    {
                      marketSectorDictionary.get(row.original.market_sectors[0])
                        ?.name
                    }
                  </Link>
                )
              case "market_sector_name":
              case "market_sector_id":
                return row.original.placeholder ? (
                  <Skeleton
                    style={{
                      width: "100px",
                      height: "14px",
                    }}
                  />
                ) : (
                  <Link
                    onClick={(e) => {
                      e.stopPropagation()
                    }}
                    href={`/markets/${row.original.market_sector_id}`}
                    className={marketSectorCss}
                    style={{
                      color: colorsV4.fg.secondary,
                    }}
                  >
                    {row.original.market_sector_name}
                  </Link>
                )
              case "listing_date":
                return (
                  <Text size={fontSize} className={tableNumberCss}>
                    {row.original.listing_date
                      ? new Date(row.original.listing_date)
                          .toISOString()
                          .split("T")[0]
                      : ""}
                  </Text>
                )
              case "chains":
                return (
                  <div className="flex flex-col gap-6 max-w-full">
                    <Text
                      size={fontSize}
                      display="flex"
                      alignItems="center"
                      onClick={(e) => {
                        e.stopPropagation()
                      }}
                    >
                      <Suspense fallback={<ProjectLogosSkeleton />}>
                        {row.original.chains?.length ? (
                          <LazyProjectChains
                            chains={row.original.chains}
                            name={row.original.name}
                            className={projectLogosRowOverride}
                          />
                        ) : null}
                      </Suspense>
                    </Text>
                  </div>
                )
              case "price_max_latest":
                return (
                  <LiveValueCell
                    fontSize={fontSize}
                    value={value}
                    projectId={row.original.project_id}
                    cellDataType={column?.cell_data_type ?? "number"}
                  />
                )
              default:
                let displayValue: string | JSX.Element = ""

                if (!field) return null

                // TODO: This is a temporary fix to show more accurate values for transaction fees
                if (field.includes("transaction_fee_")) {
                  displayValue =
                    CHART_VALUE_CELL_DATA_TYPES.accurateCurrencyThreeDecimals(
                      value
                    )
                } else {
                  displayValue = CHART_VALUE_CELL_DATA_TYPES[
                    column?.cell_data_type ?? "number"
                  ](value, numberFormat ?? DEFAULT_NUMBER_FORMAT)
                }

                return (
                  <Text size={fontSize} className={tableNumberCss}>
                    <Box as="span" className={overflowTextCss}>
                      {displayValue === "0" || displayValue === "0.0%"
                        ? ""
                        : displayValue}
                    </Box>
                  </Text>
                )
            }
          },
        }
      })
      .filter((c) => c !== null) as ColumnDef<any, any>[]
  }, [
    columns,
    projectLogoSize,
    filters.columns,
    fontSize,
    miniChartHeight,
    numberFormat,
    marketSectorDictionary,
    metricDictionary,
  ])

  const isMobile = useIsMobile()

  const table = useReactTable({
    data: projects,
    columns: tableColumns,
    getCoreRowModel: getCoreRowModel(),
    columnResizeMode: "onChange",
    state: {
      sorting: filters.sorting,
      columnPinning: {
        left: isMobile ? [] : ["name"],
      },
      columnSizing,
    },
    initialState: {
      columnSizing,
    },
    onSortingChange: (sortingUpdater) => {
      tableContainerRef.current?.scrollTo({ top: 0, behavior: "instant" })

      const newSortVal =
        typeof sortingUpdater === "function"
          ? sortingUpdater(filters.sorting)
          : sortingUpdater

      setFilters((prev) => ({ ...prev, sorting: newSortVal }))
    },
    onColumnSizingChange: (columnSizingUpdater) => {
      const newColumnSizing =
        typeof columnSizingUpdater === "function"
          ? columnSizingUpdater(columnSizing)
          : columnSizingUpdater

      setColumnSizing(newColumnSizing)
    },
  })

  const { rows } = table.getRowModel()

  const visibleColumns = table.getVisibleLeafColumns()

  const columnVirtualizer = useVirtualizer({
    count: visibleColumns.length,
    estimateSize: (index) => visibleColumns[index].getSize(),
    getScrollElement: () => tableContainerRef.current,
    horizontal: true,
    overscan: 10,
    rangeExtractor: (range) => {
      const indexes = defaultRangeExtractor({
        ...range,
        startIndex:
          range.startIndex % 2 === 0
            ? range.startIndex
            : Math.max(0, range.startIndex - 1),
      })

      return [0, ...indexes].filter((value, index, self) => {
        return self.indexOf(value) === index
      })
    },
  })

  const rowVirtualizer = useWindowVirtualizer({
    count: rows.length,
    estimateSize: () => height,
    measureElement:
      typeof window !== "undefined" &&
      navigator.userAgent.indexOf("Firefox") === -1
        ? (element) => element?.getBoundingClientRect().height
        : undefined,
    overscan: 50,
    rangeExtractor: (range) => {
      const indexes = defaultRangeExtractor({
        ...range,
        startIndex:
          range.startIndex % 2 === 0
            ? range.startIndex
            : Math.max(0, range.startIndex - 1),
      })

      return indexes
    },
  })

  const virtualColumns = columnVirtualizer.getVirtualItems()
  const virtualRows = rowVirtualizer.getVirtualItems()

  let virtualPaddingLeft: number | undefined
  let virtualPaddingRight: number | undefined

  if (columnVirtualizer && virtualColumns?.length) {
    virtualPaddingLeft = virtualColumns[0]?.start ?? 0
    virtualPaddingRight =
      columnVirtualizer.getTotalSize() -
      (virtualColumns[virtualColumns.length - 1]?.end ?? 0)
  }

  const fetchMore = useCallback(() => {
    if (isFetching) return

    if (hasMore) {
      setIsFetching(true)

      fetchProjectsList(filters, ROWS_PER_FETCH, offset).then((data) => {
        setIsFetching(false)
        if (!data) return

        if (!data.data.length) {
          setHasMore(false)
          return
        }

        const newOffset = offset + ROWS_PER_FETCH
        setOffset(newOffset)

        setProjects((prev) => [...prev, ...data.data])

        setHasMore(newOffset <= data.rows_before_limit_at_least!)
      })
    }
  }, [isFetching, setIsFetching, offset, filters, hasMore])

  const { ref: intersectionRef } = useIntersectionObserver({
    threshold: 0,
    rootMargin: "20%",
    onChange: (isIntersecting) => {
      if (isIntersecting) {
        fetchMore()
      }
    },
  })

  if (projects.length === 0 && !isFetching) {
    return (
      <EmptyState
        icon={MagnifyingGlass}
        headingText="No projects found"
        bodyText="There are no projects that match your search or filters."
      />
    )
  }

  return (
    <Box ref={tableContainerRef} className={tableContainerCss}>
      <CoreTable.Table
        width="100%"
        rowHover
        rowBorderRadius="none"
        density={compact ? "small" : "medium"}
        style={{
          tableLayout: "fixed",
        }}
      >
        <CoreTable.TableHeader className={projectListHeaderCss}>
          <VirtualPadding padding={virtualPaddingLeft} />
          {table.getHeaderGroups().map((headerGroup) => (
            <CoreTable.Row key={headerGroup.id}>
              {virtualColumns.map((virtualColumn) => {
                const header = headerGroup.headers[virtualColumn.index]
                return (
                  <TableHeader
                    key={header.column.id}
                    table={table}
                    header={header}
                    filtersAtom={filtersAtom}
                    columns={columns}
                    compact={compact}
                    isSticking={(columnVirtualizer.scrollOffset || 0) > 0}
                  />
                )
              })}
            </CoreTable.Row>
          ))}
          <VirtualPadding padding={virtualPaddingRight} />
        </CoreTable.TableHeader>
        <CoreTable.TableBody
          style={{
            position: "relative",
            height: `${rowVirtualizer.getTotalSize()}px`,
            display: "block",
          }}
        >
          {virtualRows.map((virtualRow, index) => {
            const row = rows[virtualRow.index]
            const visibleCells = row.getVisibleCells()

            return (
              <CoreTable.Row
                key={row.id}
                data-index={virtualRow.index}
                ref={(node) => rowVirtualizer.measureElement(node)}
                style={{
                  height: `${height}px`,
                  position: "absolute",
                  transform: `translateY(${virtualRow.start}px)`,
                  minWidth: "100%",
                }}
              >
                <VirtualPadding padding={virtualPaddingLeft} />
                {virtualColumns.map((vc, i) => {
                  const cell = visibleCells[vc.index]
                  const isPinned = cell.column.getIsPinned()
                  const isLastLeftPinnedColumn =
                    isPinned === "left" && cell.column.getIsLastColumn("left")
                  const isFirstRightPinnedColumn =
                    isPinned === "right" &&
                    cell.column.getIsFirstColumn("right")
                  return (
                    <CoreTable.Cell
                      key={cell.id}
                      data-pinned={isPinned}
                      data-is-sticking={
                        (columnVirtualizer.scrollOffset || 0) > 0
                      }
                      className={clsx([
                        projectListCellCss,
                        {
                          [leftPinnedColumnCss]: isLastLeftPinnedColumn,
                          [rightPinnedColumnCss]: isFirstRightPinnedColumn,
                        },
                      ])}
                      style={{
                        ...getCommonPinningStyles(cell.column),
                        height: `${height - 1}px`,
                      }}
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </CoreTable.Cell>
                  )
                })}
                <VirtualPadding padding={virtualPaddingRight} />
              </CoreTable.Row>
            )
          })}
        </CoreTable.TableBody>
      </CoreTable.Table>
      {hasMore && (
        <div
          className="flex justify-center w-full"
          style={{ height: 60 }}
          ref={intersectionRef}
        >
          <div className="text-sm text-fg-secondary">
            Loading more projects...
          </div>
        </div>
      )}
    </Box>
  )
}
